// client/src/pages/FarmerDashboard.tsx
import React, { useState, useEffect, useCallback, useMemo, memo } from "react";
import { sanitizeInput, validateUserId } from "../utils/security.ts";
import {
  Map,
  Layers,
  Globe,
  Clock,
  Camera,
  Cloud,
  Mic,
  Thermometer,
  AlertTriangle,
  MessageCircle,
  BookOpen,
  Users,
} from "lucide-react";
import StatCard from "../components/StatCard.tsx";
import FeatureCard from "../components/FeatureCard.tsx";
import { useLanguage } from "../contexts/LanguageContext.tsx";
import AddFarmModal from "../components/AddFarmModal.tsx";
import AddCropHealthReportModal from "../components/AddCropHealthReportModal.tsx";
import AddPestWeatherAlertModal from "../components/AddPestWeatherAlertModal.tsx";
import UserOnboarding from "../components/UserOnboarding.tsx";
import FeedbackWidget from "../components/FeedbackWidget.tsx";
import CropAnalysisModal from "../components/CropAnalysisModal.tsx";
import CropAnalysisHistoryModal from "../components/CropAnalysisHistoryModal.tsx";
import CreatePestAlertModal from "../components/CreatePestAlertModal.tsx";
import CommunityDataPanel from "../components/CommunityDataPanel.tsx";

import ProductionAIAdvisor from "../components/ProductionAIAdvisor.tsx";
import CropHealth from "./CropHealth.tsx";
import Weather from "./Weather.tsx";
import SimpleMarketPanel from "../components/SimpleMarketPanel.tsx";
import BottomNavigation from "../components/BottomNavigation.tsx";
import YieldForecastPanel from "../components/YieldForecastPanel.tsx";
import IrrigationPanel from "../components/IrrigationPanel.tsx";

import SoilManagementPanel from "../components/SoilManagementPanel.tsx";
import FertilizerPlannerPanel from "../components/FertilizerPlannerPanel.tsx";
import AdminDashboard from "./AdminDashboard.tsx";
import FarmerMessages from "../components/FarmerMessages.tsx";

import { offlineData } from "../lib/offlineData.ts";
import { supabase } from "../lib/supabaseClient.ts";

import type { User } from "../types.ts";

interface FarmerDashboardProps {
  online: boolean;
  user: User | null;
  isOfflineMode: boolean;
  onSignIn?: () => void;
}

function isAdmin(user: User | null): boolean {
  if (!user?.id || !validateUserId(user.id)) return false;
  return user?.user_metadata?.role === "admin";
}

function getFeatureCards(
  user: User | null,
  openCropAnalysis: () => void,
  setTab: (tab: number) => void,
  t: (key: string) => string
) {
  const handleWeather = () => {
    setTab(2); // Weather tab
  };

  const handleCommunityData = () => {
    setTab(4); // Community Data tab
  };

  const handleVoiceQuery = () => {
    setTab(3); // AI Advisor tab with voice activation
    setTimeout(() => {
      const micButton = document.querySelector(
        'button[title*="Record"]'
      ) as HTMLButtonElement;
      if (micButton && !micButton.disabled) {
        micButton.click();
      }
    }, 500);
  };

  return [
    {
      icon: <Camera className="w-8 h-8 mx-auto mb-2 text-green-600" />,
      title: t("features.cropAnalysis"),
      desc: user ? undefined : t("features.signInRequired"),
      onClick: user ? openCropAnalysis : undefined,
    },
    {
      icon: <Cloud className="w-8 h-8 mx-auto mb-2 text-blue-600" />,
      title: t("features.weather"),
      onClick: handleWeather,
    },
    {
      icon: <Users className="w-8 h-8 mx-auto mb-2 text-blue-600" />,
      title: "Community Data",
      desc: user ? undefined : t("features.signInRequired"),
      onClick: user ? handleCommunityData : undefined,
    },
    {
      icon: <Mic className="w-8 h-8 mx-auto mb-2 text-purple-600" />,
      title: t("features.voiceQuery"),
      desc: user ? undefined : t("features.signInRequired"),
      onClick: user ? handleVoiceQuery : undefined,
    },
    {
      icon: <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-red-600" />,
      title: "Alerts",
      desc: user ? undefined : t("features.signInRequired"),
      onClick: user ? () => setTab(1) : undefined,
    },
    {
      icon: <MessageCircle className="w-8 h-8 mx-auto mb-2 text-indigo-600" />,
      title: "Messages",
      desc: user ? undefined : t("features.signInRequired"),
      onClick: user ? () => setTab(5) : undefined,
    },
    {
      icon: <BookOpen className="w-8 h-8 mx-auto mb-2 text-orange-600" />,
      title: "Resources",
      desc: user ? undefined : t("features.signInRequired"),
      onClick: user ? () => setTab(5) : undefined,
    },
  ];
}

type Farm = {
  id: string;
  name: string;
  location: string | null;
  area: number | null;
  created_at: string;
};

const WeatherStat = memo(function WeatherStat({
  icon,
  value,
  label,
}: {
  icon: React.ReactNode;
  value: string | number;
  label: string;
}) {
  return (
    <div className="p-3 text-center rounded-lg bg-green-50">
      {icon}
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-green-700">{label}</p>
    </div>
  );
});

interface WeatherData {
  temperature: number;
  humidity: number;
  windSpeed: number;
  visibility: number;
}

function useWeatherData() {
  const [weather, setWeather] = useState<WeatherData>({
    temperature: 0,
    humidity: 0,
    windSpeed: 0,
    visibility: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        // Set loading to false immediately to show UI
        setLoading(false);
        
        const { weatherService } = await import(
          "../services/weatherService.js"
        );
        const data = await weatherService.getWeatherData("Maseru, Lesotho");
        console.log("Dashboard weather data:", data);
        setWeather({
          temperature: data.temperature,
          humidity: data.humidity,
          windSpeed: data.windSpeed,
          visibility: data.visibility || 10,
        });
      } catch (error) {
        console.error("Dashboard weather failed:", error);
        setWeather({
          temperature: 22,
          humidity: 65,
          windSpeed: 8,
          visibility: 10,
        });
      }
    };

    // Start weather fetch in background
    setTimeout(fetchWeather, 100);
  }, []);

  return { weather, loading };
}

function useDashboardData() {
  const [alerts, setAlerts] = useState({ count: 0, weather: 0, pest: 0 });
  const [marketPrice, setMarketPrice] = useState({
    price: 0,
    commodity: "Maize",
    loading: true,
  });
  const [recentActivity, setRecentActivity] = useState<
    {
      type: string;
      title: string;
      description: string;
      time: string;
      color: string;
    }[]
  >([]);

  useEffect(() => {
    // Fetch alerts count
    const fetchAlerts = async () => {
      try {
        // Simulate alerts data - replace with real API
        setAlerts({ count: 3, weather: 2, pest: 1 });
      } catch (error) {
        console.error("Failed to fetch alerts:", error);
      }
    };

    // Fetch market data with timeout
    const fetchMarketData = async () => {
      try {
        const controller = new AbortController();
        setTimeout(() => controller.abort(), 2000); // 2 second timeout
        
        const response = await fetch("/api/market-prices", {
          signal: controller.signal
        });
        const data = await response.json();
        const maizePrice = data.grains?.items?.find(
          (item: { name: string; price: number }) =>
            item.name.toLowerCase().includes("maize")
        );
        setMarketPrice({
          price: maizePrice?.price || 8.5,
          commodity: "Maize",
          loading: false,
        });
      } catch (error) {
        console.error("Failed to fetch market data:", error);
        setMarketPrice({ price: 8.5, commodity: "Maize", loading: false });
      }
    };

    // Fetch recent activity
    const fetchActivity = async () => {
      const activities = [
        {
          type: "weather",
          title: "Weather alert issued",
          description: "Heavy rain expected tomorrow",
          time: "2h ago",
          color: "green",
        },
        {
          type: "market",
          title: "Market prices updated",
          description: "Maize prices increased by 5%",
          time: "4h ago",
          color: "blue",
        },
        {
          type: "analysis",
          title: "Crop analysis completed",
          description: "Healthy growth detected",
          time: "1d ago",
          color: "purple",
        },
      ];
      setRecentActivity(activities);
    };

    // Run all fetches in parallel without blocking
    Promise.allSettled([
      fetchAlerts(),
      fetchMarketData(),
      fetchActivity()
    ]);
  }, []);

  return { alerts, marketPrice, recentActivity };
}

export default function FarmerDashboard({
  online,
  user,
  isOfflineMode,
  onSignIn,
}: FarmerDashboardProps) {
  const { t } = useLanguage();
  const [tab, setTab] = useState(0);
  const { weather, loading: weatherLoading } = useWeatherData();
  const { alerts, marketPrice, recentActivity } = useDashboardData();

  const isUserAdmin = useMemo(() => isAdmin(user), [user]);

  const [unreadCount, setUnreadCount] = useState(0);

  // Simple unread count management
  useEffect(() => {
    const handleMessagesRead = () => {
      setUnreadCount(0);
    };

    const handleMessageReceived = () => {
      setUnreadCount((prev) => prev + 1);
    };

    // Listen for events
    window.addEventListener("messagesRead", handleMessagesRead);
    window.addEventListener("messageReceived", handleMessageReceived);

    return () => {
      window.removeEventListener("messagesRead", handleMessagesRead);
      window.removeEventListener("messageReceived", handleMessageReceived);
    };
  }, []);

  const tabs = [
    t("dashboard.title"),
    t("dashboard.cropHealth"),
    t("dashboard.weather"),
    t("dashboard.aiAdvisor"),
    "Community Data",
    `Messages${
      unreadCount > 0 ? ` (${unreadCount > 99 ? "99+" : unreadCount})` : ""
    }`,
    t("dashboard.marketInfo"),
    t("dashboard.yieldForecast"),
    t("dashboard.irrigation"),
    t("dashboard.soilManagement"),
    t("dashboard.fertilizerPlanner"),
    ...(isUserAdmin ? ["Admin Dashboard"] : []),
  ];

  const [farms, setFarms] = useState<Farm[]>([]);
  const [showAddFarm, setShowAddFarm] = useState(false);
  const [showAddReport, setShowAddReport] = useState(false);

  const [showAddAlert, setShowAddAlert] = useState(false);
  const [showCropAnalysis, setShowCropAnalysis] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showCreateAlert, setShowCreateAlert] = useState(false);

  // Check if user needs onboarding (only for users with no farms)
  useEffect(() => {
    const checkOnboarding = async () => {
      if (!user?.id) return;

      // Check if user has completed onboarding
      const userOnboardingCompleted = localStorage.getItem(
        `onboarding_completed_${user.id}`
      );
      if (userOnboardingCompleted) return;

      // Skip onboarding check if offline
      if (!online || isOfflineMode) {
        return;
      }

      // Check if user has any farms
      try {
        const { data: userFarms, error } = await supabase
          .from("farms")
          .select("id")
          .eq("user_id", user.id)
          .limit(1);

        if (error) {
          console.error("Error checking farms:", error);
          return; // Don't show onboarding on error
        }

        // Only show onboarding if user has no farms
        if (!userFarms || userFarms.length === 0) {
          setShowOnboarding(true);
        }
      } catch (error) {
        console.error("Error checking farms:", error);
        // Don't show onboarding on network errors
      }
    };

    checkOnboarding();
  }, [user, online, isOfflineMode]);

  const fetchFarms = useCallback(async (): Promise<Farm[]> => {
    if (!user?.id) {
      return [];
    }

    if (isOfflineMode || !online) {
      const offlineFarms = offlineData.getFarms(user.id);
      if (offlineFarms.length === 0) {
        offlineData.generateSampleData(user.id);
        return offlineData.getFarms(user.id).map((farm) => ({
          id: farm.id,
          name: farm.name,
          location: farm.location,
          area: farm.size,
          created_at: farm.createdAt,
        }));
      }
      return offlineFarms.map((farm) => ({
        id: farm.id,
        name: farm.name,
        location: farm.location,
        area: farm.size,
        created_at: farm.createdAt,
      }));
    }

    try {
      const { data, error } = await supabase
        .from("farms")
        .select("id, name, location, area, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) throw error;

      return (
        data?.map((farm) => ({
          ...farm,
          id: farm.id as string,
          area: farm.area as number | null,
          created_at: farm.created_at as string,
          name: sanitizeInput((farm.name as string) || ""),
          location: farm.location
            ? sanitizeInput(farm.location as string)
            : null,
        })) || []
      );
    } catch (error) {
      console.warn("Online fetch failed, using offline data:", error);
      const offlineFarms = offlineData.getFarms(user.id);
      if (offlineFarms.length === 0) {
        offlineData.generateSampleData(user.id);
      }
      return offlineFarms.map((farm) => ({
        id: farm.id,
        name: farm.name,
        location: farm.location,
        area: farm.size,
        created_at: farm.createdAt,
      }));
    }
  }, [user, isOfflineMode, online]);

  const [loading, setLoading] = useState(true);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const loadFarms = async () => {
      setLoading(true);
      try {
        const farmsData = await fetchFarms();
        setFarms(farmsData);
      } catch (error) {
        console.error("Error loading farms:", error);
        setFarms([]);
      } finally {
        setLoading(false);
      }
    };

    if (user?.id) {
      loadFarms();
    }
  }, [user?.id, fetchFarms]);

  const farmStats = useMemo(() => {
    // Triple safety check for farms array
    let farmsArray: Farm[] = [];

    try {
      if (farms && Array.isArray(farms)) {
        farmsArray = farms;
      } else if (farms && typeof farms === "object" && "length" in farms) {
        farmsArray = Array.from(farms as ArrayLike<Farm>);
      }
    } catch (error) {
      console.error("Error processing farms:", error);
      farmsArray = [];
    }

    const totalFarms = farmsArray.length;
    const totalArea =
      farmsArray.length > 0
        ? farmsArray.reduce((sum, farm) => {
            try {
              return sum + (farm?.area ? Number(farm.area) : 0);
            } catch {
              return sum;
            }
          }, 0)
        : 0;
    const uniqueRegions =
      farmsArray.length > 0
        ? new Set(farmsArray.map((farm) => farm?.location || "")).size
        : 0;
    const mostRecentFarm =
      farmsArray.length > 0 ? farmsArray[0]?.name || "-" : "-";

    return { totalFarms, totalArea, uniqueRegions, mostRecentFarm };
  }, [farms]);

  const stats = useMemo(
    () => [
      {
        icon: <Map />,
        label: t("stats.activeFarms"),
        value: loading ? "…" : farmStats.totalFarms,
        color: "green" as const,
      },
      {
        icon: <Layers />,
        label: t("stats.totalArea"),
        value: loading ? "…" : `${farmStats.totalArea} ${t("common.acres")}`,
        color: "emerald" as const,
      },
      {
        icon: <Globe />,
        label: t("stats.regions"),
        value: loading ? "…" : farmStats.uniqueRegions,
        color: "blue" as const,
      },
      {
        icon: <Clock />,
        label: t("stats.mostRecentFarm"),
        value: loading ? "…" : farmStats.mostRecentFarm,
        color: "amber" as const,
      },
    ],
    [farmStats, loading, t]
  );

  const featureCards = useMemo(
    () => getFeatureCards(user, () => setShowCropAnalysis(true), setTab, t),
    [user, t]
  );

  const isLoggedIn = useMemo(() => !!user, [user]);

  const displayName = useMemo(() => {
    if (!user) return "";

    // Dashboard user object has 'name' field, not 'user_metadata.full_name'
    return user.name || "";
  }, [user]);

  const handleTabChange = useCallback(
    (tabIndex: number) => {
      if (tabIndex >= 0 && tabIndex < tabs.length) {
        setTab(tabIndex);
      }
    },
    [tabs.length]
  );

  return (
    <div className="flex flex-col pt-20 bg-green-50" role="main">
      {isLoggedIn && (
        <div className="flex justify-start px-2 pt-3 mb-1 sm:px-4">
          <span className="inline-flex items-center px-2 py-3 text-xs font-semibold text-green-800 rounded sm:text-sm">
            {t("common.welcome")}, {displayName}
          </span>
        </div>
      )}

      {/* App Summary */}
      <div className="mt-6 mb-8 text-center">
        <h2 className="mb-2 text-2xl font-bold text-green-800">
          {t("header.tagline")}
        </h2>
        <p className="max-w-2xl mx-auto text-green-800">
          {t("footer.empowering")}
        </p>
        <div className="mt-2 text-xs text-gray-400">
          v1.0.2 - Updated
        </div>

      </div>

      <div className="flex justify-center mt-6 mb-8">
        {online && !isOffline ? (
          <div className="inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold mb-4 bg-green-600 text-white border-green-400 shadow-sm">
            {t("common.realTimeData")}
          </div>
        ) : (
          <div className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium mb-4 bg-orange-100 text-orange-900 border-orange-400 shadow-sm">
            📱{" "}
            {isOfflineMode ? t("offline.offline") : t("offline.dataFromCache")}{" "}
            - {t("common.cachedData")}
          </div>
        )}
      </div>
      <section className="px-2 mb-8 sm:px-4">
        <h3 className="mb-2 text-base font-bold text-green-800 sm:text-lg">
          {t("common.statistics")}
        </h3>
        <div className="grid grid-cols-2 gap-3 sm:gap-6 lg:grid-cols-4">
          {stats.map((stat) => (
            <StatCard key={stat.label} {...stat} />
          ))}
        </div>
      </section>
      <section className="px-2 mb-8 sm:px-4">
        <h3 className="mb-2 text-base font-bold text-green-800 sm:text-lg">
          {t("common.features")}
        </h3>
        <div className="grid grid-cols-2 gap-3 sm:gap-6 lg:grid-cols-4">
          {featureCards.slice(0, 4).map((f) => (
            <FeatureCard key={f.title} {...f} onClick={f.onClick} />
          ))}
        </div>
        <div className="grid grid-cols-3 gap-3 mt-4 sm:gap-6">
          {featureCards.slice(4).map((f) => (
            <FeatureCard key={f.title} {...f} onClick={f.onClick} />
          ))}
        </div>
      </section>

      {/* Fixed Tabs - Always visible */}
      {/* MODIFIED: The sticky container no longer has shadow or bottom border. */}
      <div className="sticky top-0 z-10 mt-8 mb-4 bg-white">
        <div className="px-2 sm:px-4">
          {/* MODIFIED: Removed the general separator line (border-b border-green-200) to ensure NO borders exist except for the active tab's bottom border. */}
          <div className="flex justify-start w-full p-0 overflow-x-auto scrollbar-hide">
            {tabs.map((tabItem, i) => {
              const label =
                typeof tabItem === "string" ? tabItem : tabItem.name;

              return (
                <button
                  key={i}
                  role="tab"
                  aria-selected={tab === i}
                  onClick={() => handleTabChange(i)}
                  className={`flex-shrink-0 whitespace-nowrap px-3 sm:px-6 h-12 text-sm font-medium transition-all relative flex items-center bg-white border-0 ${
                    tab === i
                      ? "text-green-700 border-b-2 border-green-600"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="px-2 pb-8 sm:px-4">
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6">
            {tab === 0 && (
              <div role="tabpanel" id="tabpanel-0" aria-labelledby="tab-0">
                <div className="space-y-6">
                  {/* Quick Overview */}
                  <div className="grid gap-4 sm:gap-6 lg:grid-cols-3">
                    <div className="border border-green-200 rounded-lg shadow-sm bg-card text-card-foreground">
                      <div className="p-6">
                        <h3 className="flex items-center gap-2 mb-3 text-lg font-semibold text-green-800">
                          <Thermometer className="w-5 h-5" />
                          Today's Weather
                        </h3>
                        <WeatherStat
                          icon={
                            <Thermometer className="w-8 h-8 mx-auto mb-2 text-green-600" />
                          }
                          value={
                            weatherLoading ? "..." : `${weather.temperature}°C`
                          }
                          label={
                            weatherLoading
                              ? "Loading..."
                              : `${weather.humidity}% humidity`
                          }
                        />
                        <button
                          onClick={() => setTab(2)}
                          className="mt-2 text-sm text-green-600 hover:text-green-700"
                        >
                          View full weather →
                        </button>
                      </div>
                    </div>

                    <div className="border border-green-200 rounded-lg shadow-sm bg-card text-card-foreground">
                      <div className="p-6">
                        <h3 className="flex items-center gap-2 mb-3 text-lg font-semibold text-green-800">
                          <AlertTriangle className="w-5 h-5" />
                          Active Alerts
                        </h3>
                        <div className="text-2xl font-bold text-gray-900">
                          {alerts.count}
                        </div>
                        <p className="text-sm text-gray-600">
                          {alerts.weather} weather, {alerts.pest} pest alert
                        </p>
                        <button
                          onClick={() => setTab(4)}
                          className="mt-2 text-sm text-green-600 hover:text-green-700"
                        >
                          View all alerts →
                        </button>
                      </div>
                    </div>

                    <div className="border border-green-200 rounded-lg shadow-sm bg-card text-card-foreground">
                      <div className="p-6">
                        <h3 className="flex items-center gap-2 mb-3 text-lg font-semibold text-green-800">
                          <Globe className="w-5 h-5" />
                          Market Prices
                        </h3>
                        <div className="text-2xl font-bold text-gray-900">
                          {marketPrice.loading
                            ? "..."
                            : `M${marketPrice.price}`}
                        </div>
                        <p className="text-sm text-gray-600">
                          {marketPrice.commodity} per kg (today)
                        </p>
                        <button
                          onClick={() => setTab(5)}
                          className="mt-2 text-sm text-green-600 hover:text-green-700"
                        >
                          View market info →
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Recent Activity */}
                  <div className="border border-green-200 rounded-lg shadow-sm bg-card text-card-foreground">
                    <div className="p-6">
                      <h3 className="mb-4 text-lg font-semibold text-green-800">
                        Recent Activity
                      </h3>
                      <div className="space-y-3">
                        {recentActivity.map((activity, index) => (
                          <div
                            key={index}
                            className={`flex items-center gap-3 p-3 bg-${activity.color}-50 rounded-lg`}
                          >
                            <div
                              className={`w-2 h-2 bg-${activity.color}-500 rounded-full`}
                            ></div>
                            <div className="flex-1">
                              <p className="text-sm font-medium">
                                {activity.title}
                              </p>
                              <p className="text-xs text-gray-600">
                                {activity.description}
                              </p>
                            </div>
                            <span className="text-xs text-gray-500">
                              {activity.time}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {tab === 1 && (
              <div role="tabpanel" id="tabpanel-1" aria-labelledby="tab-1">
                <CropHealth user={user} onSignIn={onSignIn} />
              </div>
            )}
            {tab === 2 && (
              <div
                className="-mx-6"
                role="tabpanel"
                id="tabpanel-2"
                aria-labelledby="tab-2"
              >
                <div className="px-2 sm:px-4">
                  <Weather />
                </div>
              </div>
            )}
            {tab === 3 && (
              <div
                className="flex flex-col"
                role="tabpanel"
                id="tabpanel-3"
                aria-labelledby="tab-3"
              >
                {isLoggedIn ? (
                  <div className="-m-6">
                    <ProductionAIAdvisor user={user} />
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-20">
                    <button
                      type="button"
                      className="px-6 py-2 text-sm font-semibold text-white transition bg-green-600 rounded-md sm:px-8 sm:text-base hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                      onClick={onSignIn}
                      aria-label={t("dashboard.signInQuestions")}
                    >
                      {t("dashboard.signInQuestions")}
                    </button>
                  </div>
                )}
              </div>
            )}
            {tab === 4 && (
              <div role="tabpanel" id="tabpanel-4" aria-labelledby="tab-4">
                <CommunityDataPanel user={user} />
              </div>
            )}
            {tab === 5 && (
              <div role="tabpanel" id="tabpanel-5" aria-labelledby="tab-5">
                {isLoggedIn ? (
                  <FarmerMessages user={user} />
                ) : (
                  <div className="flex items-center justify-center py-20">
                    <button
                      type="button"
                      className="px-6 py-2 text-sm font-semibold text-white transition bg-green-600 rounded-md sm:px-8 sm:text-base hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                      onClick={onSignIn}
                    >
                      Sign in to view messages
                    </button>
                  </div>
                )}
              </div>
            )}
            {tab === 6 && (
              <div role="tabpanel" id="tabpanel-6" aria-labelledby="tab-6">
                <div className="px-2 py-8 sm:px-4">
                  <SimpleMarketPanel />
                </div>
              </div>
            )}
            {tab === 7 && (
              <div role="tabpanel" id="tabpanel-7" aria-labelledby="tab-7">
                <div className="px-2 py-8 sm:px-4">
                  <YieldForecastPanel user={user} />
                </div>
              </div>
            )}
            {tab === 8 && (
              <div
                className="-mx-6"
                role="tabpanel"
                id="tabpanel-8"
                aria-labelledby="tab-8"
              >
                {Array.isArray(farms) && farms.length > 0 && farms[0] ? (
                  <IrrigationPanel
                    farmId={farms[0].id}
                    onRainTomorrow={false}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center px-6 py-12 text-center text-gray-600">
                    <p className="mb-4">{t("dashboard.noFarmSelected")}</p>
                    {user && (
                      <p className="text-sm text-gray-500">
                        Add a farm to use irrigation management
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
            {tab === 9 && (
              <div role="tabpanel" id="tabpanel-9" aria-labelledby="tab-9">
                <div className="px-2 py-8 sm:px-4">
                  <SoilManagementPanel user={user} onSignIn={onSignIn} />
                </div>
              </div>
            )}
            {tab === 10 && (
              <div role="tabpanel" id="tabpanel-10" aria-labelledby="tab-10">
                <div className="px-2 py-8 sm:px-4">
                  <FertilizerPlannerPanel user={user} onSignIn={onSignIn} />
                </div>
              </div>
            )}
            {tab === 11 && isUserAdmin && (
              <div role="tabpanel" id="tabpanel-11" aria-labelledby="tab-11">
                <AdminDashboard user={user} />
              </div>
            )}
          </div>
        </div>
      </div>

      {isLoggedIn && user?.id && validateUserId(user.id) && (
        <>
          <AddCropHealthReportModal
            open={showAddReport}
            onClose={() => setShowAddReport(false)}
            userId={user.id}
            farms={farms}
            onReportAdded={() => {}}
          />
          <CropAnalysisModal
            open={showCropAnalysis}
            onClose={() => setShowCropAnalysis(false)}
            user={user}
          />
          <CropAnalysisHistoryModal
            open={showHistory}
            onClose={() => setShowHistory(false)}
            user={user}
            isAdmin={isUserAdmin}
            onSignIn={onSignIn}
          />
          <CreatePestAlertModal
            open={showCreateAlert}
            onClose={() => setShowCreateAlert(false)}
            onAlertCreated={() => {
              setShowCreateAlert(false);
              // Refresh alerts by triggering a re-fetch
              window.dispatchEvent(new CustomEvent("refreshAlerts"));
            }}
          />
        </>
      )}

      {isLoggedIn && isUserAdmin && user?.id && validateUserId(user.id) && (
        <>
          <AddPestWeatherAlertModal
            open={showAddAlert}
            onClose={() => setShowAddAlert(false)}
            userId={user.id}
            farms={farms}
            onAlertAdded={() => setShowAddAlert(false)}
          />
          <AddFarmModal
            open={showAddFarm}
            onClose={() => setShowAddFarm(false)}
            onFarmAdded={fetchFarms}
            userId={user.id}
          />
        </>
      )}

      {/* User Onboarding */}
      {showOnboarding && user && (
        <UserOnboarding
          user={user}
          onComplete={async () => {
            setShowOnboarding(false);
            // Force direct fetch from Supabase bypassing cache
            try {
              const { data } = await supabase
                .from("farms")
                .select("id, name, location, area, created_at")
                .eq("user_id", user.id)
                .order("created_at", { ascending: false });

              if (data) {
                const formattedFarms = data.map((farm) => ({
                  ...farm,
                  id: farm.id as string,
                  area: farm.area as number | null,
                  created_at: farm.created_at as string,
                  name: farm.name || "",
                  location: farm.location || null,
                }));
                setFarms(formattedFarms);
                console.log("Farms updated after onboarding:", formattedFarms);
              }
            } catch (error) {
              console.error("Error fetching farms after onboarding:", error);
            }
          }}
        />
      )}

      {/* Feedback Widget */}
      <FeedbackWidget user={user} />

      {/* Bottom Navigation for Mobile */}
      <BottomNavigation
        activeTab={tab}
        onTabChange={setTab}
        onCropAnalysis={() => {
          if (user) {
            setShowCropAnalysis(true);
          }
        }}
        onVoiceQuery={() => {
          if (user) {
            setTab(3);
            setTimeout(() => {
              const micButton = document.querySelector(
                'button[title*="Record"]'
              ) as HTMLButtonElement;
              if (micButton && !micButton.disabled) {
                micButton.click();
              }
            }, 500);
          }
        }}
        user={user}
      />
    </div>
  );
}
